

import React, { useState } from 'react';
import {
    type ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
    type SortingState,
    type ColumnFiltersState,
    type VisibilityState,
    type Row,
    type Column,
    type HeaderGroup,
    type Header,
    type Cell,
} from "@tanstack/react-table";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/shared/ui/table";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "@/shared/ui/dropdown-menu";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { MoreHorizontal, Settings2 } from "lucide-react";
import { Craftsman, CraftsmanUpdate, CraftsmanInsert } from '@/types';
import CraftsmanFormDialog from './CraftsmanFormDialog';
import { toast } from 'sonner';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { columns as craftsmanColumns } from './CraftsmenColumns';

// 職人一覧のprops
interface CraftsmenTableProps {
    data: Craftsman[];
    isLoading: boolean;
    onEdit: (id: string, data: CraftsmanUpdate) => Promise<boolean>;
    onDelete: (id: string) => Promise<boolean>;
}

/**
 * 職人一覧
 * @param data 職人一覧
 * @param isLoading ローディング中かどうか
 * @param onEdit 職人の編集処理
 * @param onDelete 職人の削除処理
 * @returns 職人一覧
 */
export default function CraftsmenTable({ data, isLoading, onEdit, onDelete }: CraftsmenTableProps) {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = useState({});

    const [editingCraftsman, setEditingCraftsman] = useState<Craftsman | null>(null);
    const [deletingCraftsmanId, setDeletingCraftsmanId] = useState<string | null>(null);

    /**
     * 列定義に行アクションのコールバックを渡すための meta を設定
     * @returns 列定義
     */
    const columns = React.useMemo<ColumnDef<Craftsman>[]>(() => craftsmanColumns.map(col => {
        if (col.id === 'actions') {
            return {
                ...col,
                cell: ({ row }: { row: Row<Craftsman> }) => {
                    const craftsman = row.original;
                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>操作</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => navigator.clipboard.writeText(craftsman.id)}>
                                    IDをコピー
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setEditingCraftsman(craftsman)}>
                                    編集
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setDeletingCraftsmanId(craftsman.id)} className="text-destructive">
                                    削除
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                }
            };
        }
        return col;
    }), []);


    const table = useReactTable<Craftsman>({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    /**
     * 職人の更新処理
     * @param formData 職人のフォームデータ
     * @returns 更新処理の成否
     */
    const handleUpdateSubmit = async (formData: CraftsmanInsert | CraftsmanUpdate): Promise<boolean> => {
        if (editingCraftsman && 'id' in formData) {
            const success = await onEdit(editingCraftsman.id, formData as CraftsmanUpdate);
            if (success) {
                setEditingCraftsman(null);
                return true;
            }
        }
        return false;
    };

    /**
     * 職人の削除処理
     */
    const handleDelete = async () => {
        if (deletingCraftsmanId !== null) {
            try {
                const success = await onDelete(deletingCraftsmanId);
                if (success) {
                    setDeletingCraftsmanId(null);
                    toast.success('職人を削除しました');
                }
            } catch (error) {
                console.error('Error deleting craftsman:', error);
                setDeletingCraftsmanId(null);
                toast.error('職人の削除に失敗しました');
            }
        }
    };

    /**
     * 職人一覧の表示
     * @returns 職人一覧
     */
    if (isLoading) {
        return (
            <div className="rounded-md border p-4">
                <div className="flex items-center py-4">
                    <div className="h-10 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                    <div className="ml-auto h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
                </div>
                <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="h-12 bg-gray-200 rounded animate-pulse"></div>
                    ))}
                </div>
                <div className="flex items-center justify-end space-x-2 py-4">
                    <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
                    <div className="h-10 bg-gray-200 rounded w-20 animate-pulse"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 py-3">
                <Input
                    placeholder="氏名で検索..."
                    value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
                    onChange={(event) =>
                        table.getColumn("name")?.setFilterValue(event.target.value)
                    }
                    className="flex-1 sm:max-w-sm"
                />
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full sm:w-auto sm:ml-auto" size="sm">
                            <span className="hidden sm:inline">表示列</span>
                            <span className="sm:hidden">列設定</span>
                            <Settings2 className="ml-2 h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {table
                            .getAllColumns()
                            .filter((column) => column.getCanHide())
                            .map((column: Column<Craftsman, unknown>) => {
                                return (
                                    <DropdownMenuCheckboxItem
                                        key={column.id}
                                        className="capitalize"
                                        checked={column.getIsVisible()}
                                        onCheckedChange={(value) =>
                                            column.toggleVisibility(!!value)
                                        }
                                    >
                                        {column.id === 'name' ? '氏名' :
                                            column.id === 'craftsman_type' ? '職人種別' :
                                                column.id === 'daily_rate' ? '日単価 (円)' :
                                                    column.id === 'is_active' ? '在籍状態' :
                                                        column.id === 'created_at' ? '登録日時' :
                                                            column.id === 'select' ? '選択' :
                                                                column.id === 'actions' ? '操作' : column.id}
                                    </DropdownMenuCheckboxItem>
                                );
                            })}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <div className="rounded-md border">
                <Table data-testid="craftsmen-table">
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup: HeaderGroup<Craftsman>) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header: Header<Craftsman, unknown>) => {
                                    return (
                                        <TableHead key={header.id} style={{ width: header.getSize() !== 0 ? header.getSize() : undefined }}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row: Row<Craftsman>) => {
                                return (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                    >
                                        {row.getVisibleCells().map((cell: Cell<Craftsman, unknown>) => {
                                            return (
                                                <TableCell key={cell.id} style={{ width: cell.column.getSize() !== 0 ? cell.column.getSize() : undefined }}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center"
                                >
                                    登録されている職人がいません。
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
                <div className="flex-1 text-sm text-muted-foreground">
                    {table.getFilteredSelectedRowModel().rows.length} 件選択中 / 全 {table.getFilteredRowModel().rows.length} 件
                </div>
                <div className="space-x-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        前へ
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        次へ
                    </Button>
                </div>
            </div>

            <CraftsmanFormDialog
                isOpen={!!editingCraftsman}
                onClose={() => setEditingCraftsman(null)}
                onSubmit={handleUpdateSubmit}
                defaultValues={editingCraftsman}
                isUpdate={true}
            />

            <AlertDialog open={deletingCraftsmanId !== null} onOpenChange={(open) => !open && setDeletingCraftsmanId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>削除確認</AlertDialogTitle>
                        <AlertDialogDescription>
                            ID: {deletingCraftsmanId} の職人データを本当に削除しますか？この操作は元に戻せません。
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeletingCraftsmanId(null)}>キャンセル</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                            削除
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
} 